import React from 'react';
import P from 'prop-types';
import nds from '@nerdwallet/base-styles/classes';

import { Box } from '@nerdwallet/currency';
import styles from './universal-text.module.less';
import { getEditingConfig } from './lib/editing-config';
import { getTextComponent } from './lib/get-text-component';
import { FontTextIcon } from './lib/assets/fontTextIcon';
import {
  getInterpolatedValues,
  useInterpolatedValue,
} from './lib/interpolation-helper';

const UniversalText = ({
  text,
  textSize,
  textType,
  textColor,
  hexColor,
  inChronicle: legacyInChronicle,
  fontWeight,
  customTextSize,
  padding,
  margin,
  groupButton,
  defaultOptions,
  outputSample,
  fontFamily,
  enableInterpolatedQueryValue,
  ...rest
}) => {
  const getAlignment = () => {
    if (groupButton?.left) {
      return 'left';
    }
    if (groupButton?.center) {
      return 'center';
    }
    if (groupButton?.right) {
      return 'right';
    }
    return 'left';
  };

  const inObviously = fontFamily === 'Obviously';
  const inChronicle = fontFamily === 'Chronicle' || legacyInChronicle;

  const textDecoration = {
    [nds('bold')]: groupButton?.bold,
    [nds('italic')]: groupButton?.italic,
    [nds('underline')]: groupButton?.underline,
    [styles.lineThrough]: groupButton?.strikethrough,
    [styles.inChronicle]: inChronicle,
    [styles.obviously]: inObviously,
  };

  const paddingType = padding?.type || 'currency';
  const marginType = margin?.type || 'currency';
  const fontWeightValue = groupButton?.bold ? 'bold' : fontWeight;
  const textTypeConfig =
    defaultOptions?.text?.textType && !textType
      ? defaultOptions?.text?.textType
      : textType || 'title';

  const interpolatedValues = getInterpolatedValues(text);

  const shouldSkipQuery =
    !enableInterpolatedQueryValue || !interpolatedValues?.length;

  const { processedText } = useInterpolatedValue({
    interpolatedValues,
    shouldSkipQuery,
    text,
  });

  const textToShow = enableInterpolatedQueryValue ? processedText : text;

  if (!text && outputSample) {
    return <FontTextIcon />;
  }

  return (
    <React.Fragment>
      <Box
        id="universal-text"
        style={{
          paddingTop: padding?.[paddingType]?.top,
          paddingRight: padding?.[paddingType]?.right,
          paddingBottom: padding?.[paddingType]?.bottom,
          paddingLeft: padding?.[paddingType]?.left,
          marginTop: margin?.[marginType]?.top,
          marginRight: margin?.[marginType]?.right,
          marginBottom: margin?.[marginType]?.bottom,
          marginLeft: margin?.[marginType]?.left,
        }}
        {...rest}
      >
        {getTextComponent(
          textToShow,
          textTypeConfig,
          textDecoration,
          fontWeightValue,
          getAlignment(),
          { hexColor, textColor },
          textSize,
          customTextSize,
          groupButton?.htmlOutput
        )}
      </Box>
    </React.Fragment>
  );
};

UniversalText.displayName = 'UniversalText';

UniversalText.propTypes = {
  children: P.node,
  text: P.string,
  textType: P.string,
  textSize: P.string,
  inChronicle: P.bool,
  fontWeight: P.oneOf(['300', '500']),
  fontFamily: P.oneOf(['Default', 'Chronicle', 'Obviously']),
  customTextSize: P.number,
  outputSample: P.bool,
  padding: P.shape({
    type: P.string,
    currency: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
    custom: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
  }),
  margin: P.shape({
    type: P.string,
    currency: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
    custom: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
  }),
  textColor: P.string,
  hexColor: P.string,
  groupButton: P.object,
  enableInterpolatedQueryValue: P.bool,
  defaultOptions: P.shape({
    text: P.shape({
      textType: P.string,
    }),
  }),
};

UniversalText.defaultProps = {
  children: null,
  text: '',
  textSize: 'default',
  inChronicle: false,
  fontFamily: 'Default',
  fontWeight: '300',
  customTextSize: 16,
  outputSample: false,
  padding: {
    type: 'currency',
    currency: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    custom: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  margin: {
    type: 'currency',
    currency: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    custom: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  textColor: 'neutral-darkest',
  enableInterpolatedQueryValue: false,
  groupButton: {
    italic: false,
    underline: false,
    strikethrough: false,
    htmlOutput: false,
    bold: false,
    left: true,
  },
};

/**
 * @typedef options
 * @property {{textType: 'title'|'text'|'accent'}} text
 * @property {boolean} disableGroup
 *
 * @param {*} componentDocument
 * @param {string} fieldLabel Field Label in the Editor, default to 'Text'
 * @param {Array<'groupButton'|'padding'|'margin'|'fontWeight'|'textSize'|'fontFamily'|'textType'|'textColor'>} allowedControlsList List of controls to be shown in the editor
 * @param {string} fieldName
 * @param {options} options
 * @returns
 */
UniversalText.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Universal Text',
  allowedControlsList = [],
  fieldName = 'universalText',
  options = {
    text: { textType: 'title' },
    disableGroup: false,
  }
) =>
  getEditingConfig(
    componentDocument,
    fieldLabel,
    allowedControlsList,
    fieldName,
    options
  );

export default UniversalText;
